import {
  Constraint_Purchase_Threshold_Counting_Attribute,
  OfferFragment,
  ProductGroupFragment,
} from '@occc/graphql';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makePurchaseConstraintOperation } from '@occc-app/offerHelpers/operations/constraints';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import { centsToDollars, dollarsToCents } from '@occc-app/lib/currency';

import { PurchaseConstraintFormValues, PurchaseConstraintItem } from './types';

export const getMinimumPurchaseConstraintOperations = (
  offerId: string,
  values: PurchaseConstraintFormValues,
  dirtyFields: DirtyFieldsType,
  offer: OfferFragment | null | undefined,
): Operation[] => {
  if (
    !isFieldDirty<PurchaseConstraintFormValues>(
      dirtyFields,
      'minimumPurchaseConstraints.isEnabled',
    ) &&
    !isFieldDirty<PurchaseConstraintFormValues>(dirtyFields, 'minimumPurchaseConstraints.items')
  ) {
    return [];
  }

  const operations: Operation[] = [];

  if (offer) {
    const purchaseThresholdConstraints = offer.constraints.filter(
      constraintHelper.isPurchaseThreshold,
    );

    if (purchaseThresholdConstraints.length > 0) {
      for (const constraint of purchaseThresholdConstraints) {
        operations.push(makeDeleteOperation('constraintDelete', constraint.id));
      }
    }
  }

  if (values.minimumPurchaseConstraints.isEnabled) {
    for (const item of values.minimumPurchaseConstraints.items) {
      const getThreshold = () => {
        if (item.countProductsBy === Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount) {
          return item.threshold ? dollarsToCents(item.threshold) : 0;
        }

        return item.threshold || 0;
      };

      operations.push(
        makePurchaseConstraintOperation(
          offerId,
          item.eligibleProductGroups.map((group) => group.id),
          item.countProductsBy,
          getThreshold(),
        ),
      );
    }
  }

  return operations;
};

export const getEligibleProductGroupsByIds = (
  eligibleProductGroupIds: string[],
  productGroups: ProductGroupFragment[] | undefined,
) => {
  return eligibleProductGroupIds.reduce((productGroupsResult, productGroupId) => {
    const productGroup = productGroups?.find((productGroup) => productGroup.id === productGroupId);

    if (productGroup) {
      productGroupsResult.push(productGroup);
    }

    return productGroupsResult;
  }, [] as ProductGroupFragment[]);
};

export const getMinimumPurchaseConstraintInitialValues = (
  offer: OfferFragment,
  productGroups: ProductGroupFragment[] | undefined,
): PurchaseConstraintFormValues => {
  const isMinimumPurchaseConstraintsEnabled = offer.constraints.some(
    constraintHelper.isPurchaseThreshold,
  );

  let minimumPurchaseConstraintsItems: PurchaseConstraintItem[] = [
    {
      threshold: undefined,
      countProductsBy: Constraint_Purchase_Threshold_Counting_Attribute.Quantity,
      eligibleProductGroups: [],
    },
  ];

  if (isMinimumPurchaseConstraintsEnabled) {
    minimumPurchaseConstraintsItems = offer.constraints
      .filter(constraintHelper.isPurchaseThreshold)
      .map((constraint) => {
        const getThreshold = () => {
          if (
            constraint.countProductsBy ===
            Constraint_Purchase_Threshold_Counting_Attribute.MoneyAmount
          ) {
            return centsToDollars(constraint.threshold);
          }

          return constraint.threshold;
        };

        return {
          id: constraint.id,
          threshold: getThreshold(),
          eligibleProductGroups: getEligibleProductGroupsByIds(
            constraint.eligibleProductGroupIds,
            productGroups,
          ),
          countProductsBy: constraint.countProductsBy,
        };
      });
  }

  return {
    minimumPurchaseConstraints: {
      isEnabled: isMinimumPurchaseConstraintsEnabled,
      items: minimumPurchaseConstraintsItems,
    },
  };
};
